import { useEffect } from 'react'
import type { ECharts, LineSeriesOption } from 'echarts'
import type { ChartAlertDraft, ChartLevelAlert } from '@/lib/chartAlerts'
import type { ScalePoint } from './chartScale'
import { chartCandleDistanceAt, type CandleGeometry } from './candleHitTest'

export interface ChartAlertControls {
  alerts: ChartLevelAlert[]
  onCreate: (draft: ChartAlertDraft) => void
  onUpdate?: (id: string, threshold: number) => Promise<unknown>
  onDelete?: (id: string) => Promise<unknown>
  isSaving?: boolean
}

export interface ChartAlertSeries {
  exchange: string
  /** The actual plotted points, in milliseconds; nulls break the line. */
  points: readonly ScalePoint[]
  candleGeometry?: readonly CandleGeometry[]
}

/** The value of a drawn segment at a time, without interpolating across missing observations. */
export function chartAlertValueAt(points: readonly ScalePoint[], timestamp: number): number | null {
  if (!points.length || !Number.isFinite(timestamp)) return null
  let low = 0
  let high = points.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (points[middle][0] < timestamp) low = middle + 1
    else high = middle
  }
  const before = points[low - 1]
  const after = points[low]
  const valid = (point: ScalePoint | undefined): point is readonly [number, number] =>
    !!point && Number.isFinite(point[0]) && point[1] != null && Number.isFinite(point[1])
  if (after?.[0] === timestamp) return valid(after) ? after[1] : null
  if (!valid(before) || !valid(after)) return null
  const weight = (timestamp - before[0]) / (after[0] - before[0])
  return before[1] * (1 - weight) + after[1] * weight
}

/** In merged mode, target the exchange whose drawn line is closest to the clicked level. */
export function chartAlertExchangeAt(series: readonly ChartAlertSeries[], timestamp: number, threshold: number, chart?: ECharts): string | null {
  if (!Number.isFinite(timestamp) || !Number.isFinite(threshold) || threshold <= 0) return null
  // A single exchange is unambiguous, including blank space beside its history.
  if (series.length === 1) return series[0].points.some((point) =>
    Number.isFinite(point[0]) && point[1] != null && Number.isFinite(point[1])) ? series[0].exchange : null
  let nearest: { exchange: string; distance: number } | null = null
  for (const entry of series) {
    if (entry.candleGeometry) {
      const distance = chartCandleDistanceAt(chart, entry.exchange, entry.candleGeometry, timestamp, threshold)
      if (distance != null && (!nearest || distance < nearest.distance)) nearest = { exchange: entry.exchange, distance }
      continue
    }
    const value = chartAlertValueAt(entry.points, timestamp)
    if (value == null) continue
    const distance = Math.abs(threshold - value)
    if (!nearest || distance < nearest.distance) nearest = { exchange: entry.exchange, distance }
  }
  return nearest?.exchange ?? null
}

export function chartAlertMarkLine(
  controls: ChartAlertControls | undefined,
  symbol: string,
  exchange: string,
  metric: 'price' | 'oi',
  market: 'spot' | 'futures' = 'futures',
): LineSeriesOption['markLine'] {
  const alerts = (controls?.alerts || []).filter((alert) => alert.status === 'active'
    && alert.symbol.toUpperCase() === symbol.toUpperCase()
    && alert.exchange.toLowerCase() === exchange.toLowerCase()
    && alert.metric === metric && (alert.market || 'futures') === market
    && Number.isFinite(alert.threshold) && alert.threshold > 0)
  return {
    silent: true,
    animation: false,
    // ECharts otherwise rounds explicit yAxis levels to two decimals, moving
    // small-token price alerts away from both their label and hit-test position.
    precision: -1,
    symbol: 'none',
    lineStyle: { color: '#fbbf24', type: 'dashed', width: 1.2, opacity: 0.9 },
    label: {
      show: true,
      position: 'insideEndTop',
      color: '#fcd34d',
      fontSize: 10,
      backgroundColor: 'rgba(8, 13, 22, 0.88)',
      padding: [3, 5],
      borderRadius: 3,
      formatter: '{b}',
    },
    data: alerts.map((alert) => ({
      name: `${exchange.toUpperCase()} ${alert.direction === 'above' ? '↑' : '↓'} ${metric === 'price' ? '$' : ''}${alert.threshold.toLocaleString(undefined, { maximumSignificantDigits: 8 })}${metric === 'oi' ? ' coins' : ''}`,
      yAxis: alert.threshold,
    })),
  }
}

/** Listen on the plot itself so empty chart space works and axis fitting stays independent. */
export function ChartLevelAlerts({ chart, metric, market = 'futures', series, controls }: {
  chart: ECharts | null
  metric: 'price' | 'oi'
  market?: 'spot' | 'futures'
  series: readonly ChartAlertSeries[]
  controls?: ChartAlertControls
}) {
  useEffect(() => {
    if (!chart || chart.isDisposed() || !controls) return
    const create = (event: { offsetX: number; offsetY: number; event: MouseEvent }) => {
      if (chart.isDisposed()) return
      const native = event.event
      if (native?.defaultPrevented || native?.shiftKey || native?.altKey || native?.ctrlKey || native?.metaKey
        || (native?.button != null && native.button !== 0)) return
      const pixel = [event.offsetX, event.offsetY]
      if (!pixel.every(Number.isFinite) || !chart.containPixel({ gridIndex: 0 }, pixel)) return
      const point = chart.convertFromPixel({ gridIndex: 0 }, pixel)
      if (!Array.isArray(point) || !point.every(Number.isFinite) || point[1] <= 0) return
      const threshold = Number(point[1].toPrecision(12))
      const exchange = chartAlertExchangeAt(series, point[0], threshold, chart)
      if (exchange) controls.onCreate({ metric, market, exchange, threshold })
    }
    const zr = chart.getZr()
    zr.on('dblclick', create)
    return () => { if (!chart.isDisposed()) zr.off('dblclick', create) }
  }, [chart, metric, market, series, controls])
  return null
}
